import type { ChangeEvent, FormEvent } from 'react';
import type { EmailVerificationInput, Maybe } from 'src/generated/graphql-viewer';

import {
  InputDescription,
  LinkStyleButton,
  PrimaryButton,
  Spinner,
  SubHeading,
  TextInput,
} from '@movingimage-evp/mi-ui-component-library';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { RegistrationField, useSendVerificationEmailMutation } from 'src/generated/graphql-viewer';
import { isValidPhoneNumber } from 'src/utils';
import { useSelectedLanguage } from 'src/viewer/providers/selected-language-provider';
import { useViewerAccess } from 'src/viewer/providers/viewer-access-provider';

import { CountrySelect } from './country-select';
import { validEmail } from '../../../utils';
import { areAllValuesEmptyStrings } from '../../../utils/are-all-values-empty-strings';
import { useFormPersistence } from '../../hooks/use-form-persistence';

import styles from './access-form.module.css';

type Props = {
  requireEmailVerification: boolean;
  fields: Maybe<RegistrationField>[];
  validationError?: string;
  onSubmit: (formData: EmailRegistrationFormData) => void;
};

export type EmailRegistrationFormData = {
  email: string;
  firstName?: string;
  lastName?: string;
  bankCode?: string;
  companyName?: string;
  country?: string;
  departmentName?: string;
  jobTitle?: string;
  phoneNumber?: string;
};

const initialFormData: EmailRegistrationFormData = {
  email: '',
};

export function EmailRegistrationForm({ requireEmailVerification, fields, validationError, onSubmit }: Props) {
  const { t } = useTranslation();
  const { webcastId = '' } = useParams();
  const { protectionToken, error, setEmailVerificationData, setError } = useViewerAccess();

  const { selectedLanguage, primaryLanguage } = useSelectedLanguage();
  const formLanguage = selectedLanguage?.code || primaryLanguage?.code;

  const { formData, handleChange } = useFormPersistence(webcastId, initialFormData, 'emailRegistrationForm');

  const [emailVerification, setEmailVerification] = useState<EmailVerificationInput['email']>();
  const [formErrors, setFormErrors] = useState<EmailRegistrationFormData>(initialFormData);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);

  const bankCode = fields.includes(RegistrationField.BANK_CODE);
  const companyName = fields.includes(RegistrationField.COMPANY_NAME);
  const country = fields.includes(RegistrationField.COUNTRY);
  const departmentName = fields.includes(RegistrationField.DEPARTMENT_NAME);
  const email = fields.includes(RegistrationField.EMAIL);
  const firstAndLastName = fields.includes(RegistrationField.FIRST_AND_LAST_NAME);
  const jobTitle = fields.includes(RegistrationField.JOB_TITLE);
  const phoneNumber = fields.includes(RegistrationField.PHONE_NUMBER);

  const [sendVerificationEmailMutation] = useSendVerificationEmailMutation();

  const handleSentEmailVerification = (event: FormEvent) => {
    event.preventDefault();

    if (!formLanguage) return;

    const errors = {
      email: !formData?.email
        ? t('viewer.views.accessForm.email.errors.emailRequired')
        : !validEmail(formData?.email)
          ? t('viewer.views.accessForm.email.errors.incorrectEmail')
          : '',
    };

    setFormErrors(errors);

    if (areAllValuesEmptyStrings(errors)) {
      setLoading(true);

      sendVerificationEmailMutation({
        variables: {
          input: {
            webcastId,
            email: formData.email,
            language: formLanguage,
          },
        },
        onCompleted: (data) => {
          setLoading(false);

          if (data.sendVerificationEmail.__typename === 'SendVerificationEmailSuccess') {
            setEmailVerification(data.sendVerificationEmail.email);
          }

          if (
            data.sendVerificationEmail.__typename === 'SendVerificationEmailProblem' &&
            data.sendVerificationEmail.code === 'EMAIL_NOT_WHITELISTED'
          ) {
            setFormErrors({ email: t('viewer.views.accessForm.email.errors.emailNotWhitelisted') });
          }
        },
      });
    }
  };

  const handleEmailAndAdditionalFieldsSubmit = (event: FormEvent) => {
    event.preventDefault();

    const { timeStamp, ...formValues } = formData;
    const errors: EmailRegistrationFormData = { email: '' };
    const copy = 'viewer.views.accessForm.email.errors';

    const values = Object.fromEntries(
      Object.entries(formValues).map(([key, value]) => [key, value.trim()])
    ) as EmailRegistrationFormData;

    if (email) {
      if (!values.email) errors.email = t(`${copy}.emailRequired`);
      else if (!validEmail(values.email)) errors.email = t(`${copy}.incorrectEmail`);
    }

    if (firstAndLastName && !values.firstName) errors.firstName = t(`${copy}.firstNameRequired`);
    if (firstAndLastName && !values.lastName) errors.lastName = t(`${copy}.lastNameRequired`);
    if (companyName && !values.companyName) errors.companyName = t(`${copy}.companyNameRequired`);
    if (departmentName && !values.departmentName) errors.departmentName = t(`${copy}.departmentNameRequired`);

    if (phoneNumber) {
      if (!values.phoneNumber) errors.phoneNumber = t(`${copy}.phoneNumberRequired`);
      else if (!isValidPhoneNumber(values.phoneNumber)) errors.phoneNumber = t(`${copy}.incorrectPhoneNumber`);
    }

    if (jobTitle && !values.jobTitle) errors.jobTitle = t(`${copy}.jobTitleRequired`);
    if (country && !values.country) errors.country = t(`${copy}.countryRequired`);
    if (bankCode && !values.bankCode) errors.bankCode = t(`${copy}.bankCodeRequired`);

    setFormErrors(errors);

    if (areAllValuesEmptyStrings(errors)) onSubmit(values);
  };

  const handleCodeSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (!emailVerification || !setEmailVerificationData) return;

    setEmailVerificationData({ email: emailVerification, verificationCode: code });
  };

  const handleCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCode(event.target.value);

    if (error) setError?.('');
  };

  const handleAlreadyRecivedCode = () => {
    const errors = {
      email: !formData?.email
        ? t('viewer.views.accessForm.email.errors.emailAlreadyProvided')
        : !validEmail(formData?.email)
          ? t('viewer.views.accessForm.email.errors.incorrectEmail')
          : '',
    };

    setFormErrors(errors);

    if (areAllValuesEmptyStrings(errors)) {
      setEmailVerification(formData.email);
    }
  };

  const handleEmailVerificationChange = (event: ChangeEvent<HTMLInputElement>) => {
    handleChange(event);

    const { name } = event.currentTarget;

    if (name) setFormErrors((prevErrors) => ({ ...prevErrors, [name]: '' }));
  };

  return (
    <>
      {requireEmailVerification && !emailVerification && (
        <form
          data-testid="email-registration-form"
          className={styles.form}
          onSubmit={handleSentEmailVerification}
          noValidate
        >
          <SubHeading>{t('viewer.views.accessForm.emailVerification.title')}</SubHeading>

          <InputDescription
            inputId="email-registration-email-input"
            label={t('viewer.views.accessForm.emailVerification.emailInputLabel')}
            errorMessage={formErrors?.email || validationError}
          >
            <TextInput
              data-testid="email-registration-email-input"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleEmailVerificationChange}
            />
          </InputDescription>

          <PrimaryButton type="submit" className={styles.accessButton} data-testid="send-verification-code-button">
            {loading ? (
              <Spinner size={18} style={{ margin: 'auto' }} />
            ) : (
              t('viewer.views.accessForm.emailVerification.sendVerificationCodeButton')
            )}
          </PrimaryButton>

          <LinkStyleButton
            type="button"
            onClick={handleAlreadyRecivedCode}
            data-testid="move-to-verification-code-button"
          >
            {t('viewer.views.accessForm.emailVerification.moveToPinCodeButton')}
          </LinkStyleButton>
        </form>
      )}

      {requireEmailVerification && emailVerification && !protectionToken && (
        <form data-testid="code-verification-form" className={styles.form} onSubmit={handleCodeSubmit} noValidate>
          <SubHeading>
            <Trans
              i18nKey="viewer.views.accessForm.emailVerificationCode.title"
              values={{ emailAddress: emailVerification }}
              components={{
                CustomButton: (
                  <LinkStyleButton
                    onClick={handleSentEmailVerification}
                    data-testid="resend-verification-code-button"
                  />
                ),
              }}
            />
          </SubHeading>

          <InputDescription
            inputId="email-registration-code-input"
            label={t('viewer.views.accessForm.emailVerificationCode.codeInputLabel')}
            errorMessage={error}
          >
            <TextInput
              data-testid="email-registration-code-input"
              type="password"
              name="code"
              value={code}
              onChange={handleCodeChange}
            />
          </InputDescription>

          <PrimaryButton className={styles.accessButton} data-testid="accept-verification-code-button">
            {loading ? (
              <Spinner size={18} style={{ margin: 'auto' }} />
            ) : fields.length > 0 ? (
              t('viewer.views.accessForm.emailVerificationCode.confirmButton')
            ) : (
              t('viewer.views.accessForm.accessButton')
            )}
          </PrimaryButton>
        </form>
      )}

      {(!requireEmailVerification || protectionToken) && fields.length > 0 && (
        <form
          data-testid="email-registration-with-additional-fields-form"
          className={styles.form}
          onSubmit={handleEmailAndAdditionalFieldsSubmit}
          noValidate
        >
          {email && (
            <InputDescription
              inputId="email-registration-email-input"
              label={t('viewer.views.accessForm.email.emailInputLabel')}
              errorMessage={formErrors?.email || validationError}
            >
              <TextInput
                data-testid="email-registration-email-input"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
              />
            </InputDescription>
          )}

          {firstAndLastName && (
            <>
              <InputDescription
                inputId="email-registration-first-name-input"
                label={t('viewer.views.accessForm.email.firstNameInputLabel')}
                errorMessage={formErrors?.firstName}
              >
                <TextInput
                  data-testid="email-registration-first-name-input"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                />
              </InputDescription>

              <InputDescription
                inputId="email-registration-last-name-input"
                label={t('viewer.views.accessForm.email.lastNameInputLabel')}
                errorMessage={formErrors?.lastName}
              >
                <TextInput
                  data-testid="email-registration-last-name-input"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                />
              </InputDescription>
            </>
          )}

          {companyName && (
            <InputDescription
              inputId="email-registration-company-name-input"
              label={t('viewer.views.accessForm.email.companyNameInputLabel')}
              errorMessage={formErrors?.companyName}
            >
              <TextInput
                data-testid="email-registration-company-name-input"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
              />
            </InputDescription>
          )}

          {departmentName && (
            <InputDescription
              inputId="email-registration-department-name-input"
              label={t('viewer.views.accessForm.email.departmentNameInputLabel')}
              errorMessage={formErrors?.departmentName}
            >
              <TextInput
                data-testid="email-registration-department-name-input"
                name="departmentName"
                value={formData.departmentName}
                onChange={handleChange}
              />
            </InputDescription>
          )}

          {jobTitle && (
            <InputDescription
              inputId="email-registration-job-title-input"
              label={t('viewer.views.accessForm.email.jobTitleInputLabel')}
              errorMessage={formErrors?.jobTitle}
            >
              <TextInput
                data-testid="email-registration-job-title-input"
                name="jobTitle"
                value={formData.jobTitle}
                onChange={handleChange}
              />
            </InputDescription>
          )}

          {country && (
            <InputDescription
              inputId="email-registration-country-input"
              label={t('viewer.views.accessForm.email.countryInputLabel')}
              errorMessage={formErrors?.country}
            >
              <CountrySelect
                data-testid="email-registration-country-input"
                name="country"
                value={formData.country}
                onChange={handleChange}
              />
            </InputDescription>
          )}

          {phoneNumber && (
            <InputDescription
              inputId="email-registration-phone-number-input"
              label={t('viewer.views.accessForm.email.phoneNumberInputLabel')}
              errorMessage={formErrors?.phoneNumber}
            >
              <TextInput
                data-testid="email-registration-phone-number-input"
                name="phoneNumber"
                type="tel"
                value={formData.phoneNumber}
                onChange={handleChange}
              />
            </InputDescription>
          )}

          {bankCode && (
            <InputDescription
              inputId="email-registration-bank-code-input"
              label={t('viewer.views.accessForm.email.bankCodeInputLabel')}
              errorMessage={formErrors?.bankCode}
            >
              <TextInput
                data-testid="email-registration-bank-code-input"
                name="bankCode"
                value={formData.bankCode}
                onChange={handleChange}
              />
            </InputDescription>
          )}

          <PrimaryButton type="submit" className={styles.accessButton} data-testid="access-event-button">
            {loading ? <Spinner size={18} style={{ margin: 'auto' }} /> : t('viewer.views.accessForm.accessButton')}
          </PrimaryButton>
        </form>
      )}
    </>
  );
}
